/**
 * Unit Command System Type Definitions
 *
 * Based on API documentation in FRONTEND_COMMAND_INTEGRATION.md
 * Version: 1.0
 * Last Updated: 2025-10-10
 */

/**
 * Command Categories
 * Defines the type of operation a command performs
 */
export type CommandCategory =
  | 'system' // System-level operations (reboot, shutdown, etc.)
  | 'application' // Application-specific commands
  | 'network' // Network configuration and diagnostics
  | 'diagnostics'; // Diagnostic and monitoring commands

/**
 * Danger Levels
 * Indicates the risk associated with executing a command
 */
export type DangerLevel =
  | 'low' // Safe operations with minimal impact
  | 'medium' // Operations that may cause temporary disruption
  | 'high' // Operations that may cause significant impact
  | 'critical'; // Dangerous operations that may cause data loss or system failure

/**
 * UI Component Types
 * Specifies which UI component should be used for parameter input
 */
export type UIComponent =
  | 'text' // Single-line text input
  | 'textarea' // Multi-line text input
  | 'number' // Numeric input
  | 'select' // Dropdown (single selection)
  | 'multiselect' // Dropdown (multiple selection)
  | 'checkbox' // Checkbox/toggle
  | 'radio'; // Radio button group

/**
 * Parameter Data Types
 * Defines the data type of a command parameter
 */
export type ParameterDataType = 'string' | 'integer' | 'boolean' | 'array' | 'object';

/**
 * Execution Status
 * Lifecycle status of a command execution
 */
export type ExecutionStatus =
  | 'pending' // Created, waiting to be sent
  | 'queued' // Published as a retained MQTT message while the unit was offline; the broker holds it until the unit reconnects
  | 'sent' // Published to MQTT topic while the unit was connected
  | 'acknowledged' // Unit received the command
  | 'completed' // Successfully executed
  | 'failed' // Execution failed
  | 'timeout'; // No response from unit within timeout period

/**
 * All execution statuses, in lifecycle order.
 *
 * Single source of truth for status pickers so a new backend status only has to be added
 * to `ExecutionStatus` once. Note that the API reports `queued` for as long as the unit
 * stays offline (up to `mqtt.commands.ttl`, currently 72 hours), after which a server-side
 * sweep moves the execution to `timeout` with an explanatory `error_message`.
 */
export const EXECUTION_STATUSES: readonly ExecutionStatus[] = [
  'pending',
  'queued',
  'sent',
  'acknowledged',
  'completed',
  'failed',
  'timeout',
] as const;

/**
 * Validation Rules
 * Server-side validation rules for parameters
 */
export interface ValidationRules {
  /** Minimum value for numbers or minimum length for strings */
  min?: number;

  /** Maximum value for numbers or maximum length for strings */
  max?: number;

  /** Minimum number of items for arrays */
  min_items?: number;

  /** Maximum number of items for arrays */
  max_items?: number;

  /** List of allowed values (enum validation) */
  allowed_values?: string[];

  /** Regular expression pattern for string validation */
  regex?: string;

  /** Custom validation rules (Laravel format) */
  [key: string]: any;
}

/**
 * Parameter Option
 * Represents a selectable option for select/multiselect parameters
 */
export interface ParameterOption {
  /** Option value (sent to API) */
  value: string | number;

  /** Option label (displayed to user) */
  label: string;

  /** Optional description/tooltip */
  description?: string;

  /** Whether this option is disabled */
  disabled?: boolean;
}

/**
 * Command Parameter
 * Defines a parameter that can be passed to a command
 */
export interface CommandParameter {
  /** Unique parameter identifier */
  id: number;

  /** Parameter name (used in API request body) */
  name: string;

  /** Human-readable parameter name for UI */
  display_name: string;

  /** Help text describing the parameter */
  description: string;

  /** Data type of the parameter */
  data_type: ParameterDataType;

  /** Whether this parameter is required */
  is_required: boolean;

  /** Default value if not provided by user */
  default_value: any;

  /** Server-side validation rules */
  validation_rules: ValidationRules;

  /** Display order in UI (1-based) */
  order: number;

  /** Suggested UI component type */
  ui_component: UIComponent;

  /** Available options for select/multiselect components */
  ui_options?: ParameterOption[];

  /** Creation timestamp */
  created_at: string;

  /** Last update timestamp */
  updated_at: string;
}

/**
 * Feedback Wait Type
 * Defines what the system should wait for after command execution
 */
export type FeedbackWaitType = 'done_message' | 'topic_message';

/**
 * Feedback Configuration
 * Defines how the system should monitor and display command feedback
 */
export interface FeedbackConfig {
  /** What to wait for after execution */
  wait_type: FeedbackWaitType;

  /** Custom topic to monitor (for topic_message type) */
  custom_topic?: string;

  /** Custom payload/message to wait for (for topic_message type) */
  custom_message?: string;

  /** Whether to show terminal output in real-time */
  show_terminal: boolean;
}

/**
 * Command
 * Represents a command that can be executed on a unit
 */
export interface Command {
  /** Unique command identifier */
  id: number;

  /** Machine-readable command name (e.g., "shutdown", "reboot") */
  name: string;

  /** Human-readable command description */
  description: string;

  /** Command category for grouping */
  category: CommandCategory;

  /** Icon name for UI display (Material-UI icon name) */
  icon?: string;

  /** Whether the command is currently available for execution */
  is_active: boolean;

  /** Whether UI should show a confirmation dialog before execution */
  confirmation_required: boolean;

  /** Risk level associated with this command */
  danger_level: DangerLevel;

  /** Whether this command requires parameters */
  requires_parameters: boolean;

  /** Display name for UI (optional, defaults to name) */
  display_name?: string;

  /** Array of parameter ids */
  parameter_ids: number[];

  /** Full parameter objects (included in API responses) */
  parameters?: CommandParameter[];

  /** Whether this command provides real-time feedback */
  feedback?: boolean;

  /** Feedback configuration for waiting and monitoring */
  feedback_config?: FeedbackConfig;

  /** Creation timestamp */
  created_at: string;

  /** Last update timestamp */
  updated_at: string;

  /**
   * Soft-delete timestamp (ISO 8601) when the command is deleted, `null` when active.
   * Always present on `/v3/api/commands` responses; deleted commands are only
   * included in the list when `with_trashed=true` is requested.
   */
  deleted_at?: string | null;
}

/**
 * Command Parameter Draft
 * A parameter defined in the UI before it exists on the server. Used by the
 * create page, which has no command id to POST parameters to yet.
 */
export interface CommandParameterDraft {
  /** Client-side identifier, never sent to the API */
  draft_id: string;
  name: string;
  display_name: string;
  description: string;
  data_type: ParameterDataType;
  is_required: boolean;
  default_value: any;
  validation_rules: ValidationRules;
  order: number;
  ui_component: UIComponent;
  ui_options?: ParameterOption[];
}

/**
 * Unit Reference
 * Minimal unit information included in execution records
 */
export interface UnitReference {
  /** Unit ID */
  id: number;

  /** Unit hostname */
  hostname: string;
}

/**
 * User Reference
 * Minimal user information included in execution records
 */
export interface UserReference {
  /** User ID */
  id: number;

  /** User full name */
  name: string;

  /** User email */
  email: string;
}

/**
 * Command Reference
 * Minimal command information included in execution records
 */
export interface CommandReference {
  /** Command ID */
  id: number;

  /** Command name */
  name: string;

  /** Command description */
  description: string;

  /** Command category */
  category: CommandCategory;

  /** Danger level */
  danger_level: DangerLevel;
}

/**
 * Command Execution
 * Represents a record of a command execution attempt
 */
export interface CommandExecution {
  /** Unique execution identifier (auto-increment) */
  id: number;

  /** Globally unique execution identifier (UUID) */
  uuid: string;

  /** Unit on which the command was executed */
  unit: UnitReference;

  /** Command that was executed */
  command: CommandReference;

  /** User who initiated the execution */
  user: UserReference;

  /** Current execution status */
  status: ExecutionStatus;

  /** Parameters passed to the command */
  parameters: Record<string, any>;

  /** Result data returned by the unit (if any) */
  result?: any;

  /** Error message if execution failed */
  error_message?: string | null;

  /** Timestamp when command was sent via MQTT */
  sent_at?: string | null;

  /** Timestamp when unit acknowledged receipt */
  acknowledged_at?: string | null;

  /** Timestamp when execution completed successfully */
  completed_at?: string | null;

  /** Timestamp when execution failed */
  failed_at?: string | null;

  /** Creation timestamp */
  created_at: string;

  /** Last update timestamp */
  updated_at: string;
}

/**
 * API Response Wrapper
 * Generic wrapper for API responses
 */
export interface ApiResponse<T> {
  /** Response data */
  data: T;

  /** Optional metadata */
  meta?: {
    /** Total number of items (for paginated lists) */
    total?: number;

    /** Current page number */
    current_page?: number;

    /** Items per page */
    per_page?: number;

    /** Last page number */
    last_page?: number;
  };
}

/**
 * API Error Response
 * Error response format
 *
 * `errors` mirrors the **flat** 422 contract the data provider emits, not Laravel's raw body.
 * Laravel sends `{ field: string[] }`; `mapLaravelErrors` in `src/providers/data/index.ts` keeps
 * only the first message per field, so every door out of the provider — `create`/`update`
 * (`validationErrorHandling`), the raw `post` passthrough (`postValidationErrorHandling`) and the
 * command methods (`handleCommandError`) — hands the app `{ field: string }` (TVR-2144/TVR-2148).
 * Change this only together with `mapLaravelErrors`; the two describe one contract.
 */
export interface ApiError {
  /** Error message */
  message: string;

  /** Validation errors (422 responses), one message per field */
  errors?: Record<string, string>;

  /** HTTP status code */
  status?: number;
}

/**
 * Command Execution Filters
 * Query parameters for filtering execution history
 */
export interface ExecutionFilters {
  /** Filter by execution status */
  status?: ExecutionStatus;

  /** Filter by command ID */
  command_id?: number;

  /** Page number for pagination */
  page?: number;

  /** Items per page */
  per_page?: number;

  /** Filter by date range */
  created_after?: string;
  created_before?: string;
}

/**
 * Command Form Values
 * Form state for command execution
 */
export interface CommandFormValues {
  /** Selected command ID */
  commandId: number;

  /** Parameter values keyed by parameter name */
  parameters: Record<string, any>;

  /** Whether confirmation was given */
  confirmed?: boolean;
}

/**
 * WebSocket Message Types
 * Real-time update message types
 */
export type WebSocketMessageType =
  | 'execution.created'
  | 'execution.sent'
  | 'execution.acknowledged'
  | 'execution.completed'
  | 'execution.failed'
  | 'execution.timeout';

/**
 * WebSocket Execution Update
 * Real-time execution status update via WebSocket
 */
export interface ExecutionUpdate {
  /** Message type */
  type: WebSocketMessageType;

  /** Execution ID */
  execution_id: number;

  /** Execution UUID */
  uuid: string;

  /** Updated status */
  status: ExecutionStatus;

  /** Updated data */
  data: Partial<CommandExecution>;

  /** Timestamp of the update */
  timestamp: string;
}
